// Logic inspired from https://github.com/TypeCtrl/ngx-emoji-mart
// The MIT License (MIT)

// Copyright (c) Scott Cooper

// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChildren,
  QueryList,
  ViewChild,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  input,
  output,
  signal
} from '@angular/core';
import { CompressedEmojiData, EmojiData, EmojiVariation, EmojiCategory } from './emoji-data/data.interfaces';
import { categories } from './emoji-data/categories';
import { intersect } from './utils';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { PrimeThemeService } from '../../themes/prime-theme.service';
import { ResizeSensor } from 'css-element-queries';

const COLONS_REGEX = /^(?:\:([^\:]+)\:)(?:\:skin-tone-(\d)\:)?$/;
const SKINS = ['1F3FA', '1F3FB', '1F3FC', '1F3FD', '1F3FE', '1F3FF'];

export interface Emoji {
  skin: 1 | 2 | 3 | 4 | 5 | 6;
}

export interface EmojiEvent {
  emoji: EmojiData;
  $event: Event;
}

export interface EmojiOutput {
  unicode?: string;
  native?: string;
  isKeyboard?: boolean;
}

export interface FrequentlyUsedEmoji {
  timesClicked: number;
  emojiString: string;
}

@Component({
  selector: 'emoji-picker',
  templateUrl: './emoji-picker.component.html',
  styleUrls: ['./emoji-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EmojiPickerComponent implements OnInit, AfterViewInit, OnDestroy {
  // Signal-based state
  activeTop = signal<string>('search-container');
  activeSkinTone = signal<number>(0);
  activeSkinToneEmoji = signal<string>('1F44D');
  activeSearchResults = signal<boolean>(false);
  categoriesList = signal<EmojiCategory[]>(JSON.parse(JSON.stringify(categories)));

  // FREQUENTLY USED
  frequentlyUsed = signal<boolean>(false);
  frequentlyUsedEmojis = signal<FrequentlyUsedEmoji[]>([]);

  // WCAG 2.4.11: tallest currently-rendered sticky category-header, so an emoji focused
  // at the top of its category isn't left underneath the header via scroll-margin-top.
  categoryHeaderHeight = signal<number>(0);

  // ACTIVE CATEGORY
  @ViewChildren('categoryReference') categoryContainer: QueryList<ElementRef>;
  @ViewChildren('categoryReference') categoryReferences: QueryList<ElementRef>;
  @ViewChildren('categoryTopLevel') categoryTopLevel: QueryList<ElementRef>;
  @ViewChildren('categoryHeader') categoryHeaders: QueryList<ElementRef>;
  @ViewChildren('linkToCategoryTopLevel') linkToCategoryTopLevel: QueryList<ElementRef>;
  @ViewChild('emojiBottomContainer') emojiBottomContainer: ElementRef;
  @ViewChild('emojiScroller') emojiScroller: ElementRef;
  @ViewChild('emojiSearchResultsContainer') emojiSearchResultsContainer: ElementRef;
  @ViewChild('emojiSearchInput') emojiSearchInput: ElementRef;
  globalInstance: any;

  observer: IntersectionObserver;
  private ngUnsubscribe: Subject<void> = new Subject();

  // SEARCH
  searchString = signal<string>('');
  uncompressed = false;
  names: { [key: string]: EmojiData } = {};
  emojis: EmojiData[] = [];
  originalPool: any = {};
  index: {
    results?: EmojiData[];
    pool?: { [key: string]: EmojiData };
    [key: string]: any;
  } = {};
  emojisList: any = {};
  emojiSearch: { [key: string]: string } = {};
  searchResults = signal<string[]>([]);
  noResults = signal<boolean>(false);

  // Signal-based inputs
  enableTwemoji = input<boolean>();

  // Signal-based outputs
  emojiOutput = output<EmojiOutput>();
  clickedEmoji: EmojiOutput = {};

  // Getter/setter for ngModel binding with searchString signal
  get searchModel(): string {
    return this.searchString();
  }

  set searchModel(value: string) {
    this.searchString.set(value);
  }

  constructor(
    public translateService: TranslateService,
    private cdRef: ChangeDetectorRef,
    public primeThemeService: PrimeThemeService
  ) {}

  ngOnInit() {
    if (!this.uncompressed) {
      let totalEmojis = [];
      this.categoriesList().forEach(obj => {
        obj.emojis.forEach(emoji => {
          emoji.native = this.unifiedToNative(emoji.unified);
          totalEmojis.push(emoji);
        });
      });
      this.uncompress(totalEmojis);
      this.uncompressed = true;
    }

    for (const emojiData of this.emojis) {
      const { shortNames, emoticons } = emojiData;
      const id = shortNames[0];
      this.emojisList[id] = this.getSanitizedData(id);
      this.originalPool[id] = emojiData;
    }

    this.setActiveTop('search-container');

    if (
      !localStorage.getItem('clComponentChatSkinTone') ||
      localStorage.getItem('clComponentChatSkinTone') === 'undefined'
    ) {
      this.activeSkinTone.set(0);
    } else {
      this.activeSkinTone.set(JSON.parse(localStorage.getItem('clComponentChatSkinTone')));
    }

    this.setActiveSkinToneEmoji(this.activeSkinTone());

    if (localStorage.getItem('clComponentChatFrequentlyUsedEmojis')) {
      this.frequentlyUsedEmojis.set(JSON.parse(localStorage.getItem('clComponentChatFrequentlyUsedEmojis')));
      this.sortFrequentlyUsedEmojis();
    }

    this.activeSearchResults.set(false);
    this.clickedEmoji.unicode = '';
    this.clickedEmoji.native = '';
    this.searchString.set('');
    this.searchResults.set([]);

    this.setActiveSkinTone(this.activeSkinTone());
  }

  ngAfterViewInit() {
    if ('IntersectionObserver' in window) {
      let options = {
        root: this.emojiScroller.nativeElement,
        rootMargin: '-1px 0px 0px 0px'
      };
      this.observer = new IntersectionObserver(entries => {
        // Observer that tracks visible categories to set the proper active anchors
        entries.forEach(entry => {
          const id = entry.target.getAttribute('id');
          if (entry.intersectionRatio > 0) {
            this.linkToCategoryTopLevel
              .find(link => link.nativeElement.id == 'link-to-' + id)
              .nativeElement.parentElement.classList.add('visible');
          } else {
            this.linkToCategoryTopLevel
              .find(link => link.nativeElement.id == 'link-to-' + id)
              .nativeElement.parentElement.classList.remove('visible');
          }
          this.setActiveCategory();
          this.cdRef.markForCheck();
        });
      }, options);

      this.categoryReferences.forEach(obj => {
        this.observer.observe(obj.nativeElement);
      });
    }

    // Defer the change to the next change detection cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      if (this.frequentlyUsedEmojis().length > 0) {
        // Hide 'recent' if it contains no emojis
        this.frequentlyUsed.set(true);
      } else {
        this.frequentlyUsed.set(false);
      }
      // Flush the [ngClass]="{ hidden: ... }" binding on the 'recent' container before reading
      // its visibility below - otherwise ensureRovingTabindex would see the pre-toggle DOM state.
      this.cdRef.detectChanges();
      this.ensureRovingTabindex(this.emojiScroller.nativeElement);
      this.ensureRovingTabindex(this.emojiSearchResultsContainer.nativeElement);

      this.updateCategoryHeaderHeight();
      new ResizeSensor(this.emojiScroller.nativeElement, this.updateCategoryHeaderHeight);
    }, 0);
  }

  // WCAG 2.4.11: re-measures whenever the scroller resizes (e.g. width change reflowing
  // a translated category label onto a second line), always using the tallest header
  // actually rendered rather than assuming every category label is the same height.
  updateCategoryHeaderHeight = () => {
    const heights = this.categoryHeaders.map(header => header.nativeElement.offsetHeight);
    this.categoryHeaderHeight.set(Math.max(0, ...heights));
  };

  ngOnDestroy() {
    if ('IntersectionObserver' in window && this.observer) {
      this.categoryReferences.forEach(obj => {
        this.observer.unobserve(obj.nativeElement);
      });
      this.observer.disconnect();
    }
    ResizeSensor.detach(this.emojiScroller.nativeElement, this.updateCategoryHeaderHeight);
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setActiveTop(activeTop: string, clicked?: boolean) {
    this.activeTop.set(activeTop);
    if (this.activeTop() === 'search-container' && clicked) {
      this.emojiSearchInput.nativeElement.focus();
    }
    this.cdRef.markForCheck();
  }

  setActiveSkinTone(activeSkinTone: number) {
    const currentCategories = this.categoriesList();
    const updatedCategories = currentCategories.map((category, i) => ({
      ...category,
      emojis: category.emojis.map((emoji, j) => {
        if (activeSkinTone > 0) {
          if (emoji.hasOwnProperty('skinVariations') && emoji.skinVariations && emoji.skinVariations.length) {
            return {
              ...emoji,
              unified: emoji.skinVariations[activeSkinTone - 1].unified,
              native: this.unifiedToNative(emoji.skinVariations[activeSkinTone - 1].unified)
            };
          }
        } else if (activeSkinTone === 0) {
          // Reset to original emoji from categories import
          return {
            ...emoji,
            unified: categories[i].emojis[j].unified,
            native: this.unifiedToNative(categories[i].emojis[j].unified)
          };
        }
        return emoji;
      })
    }));

    this.categoriesList.set(updatedCategories);
    this.activeSkinTone.set(activeSkinTone);
    localStorage.setItem('clComponentChatSkinTone', JSON.stringify(activeSkinTone));
    this.setActiveSkinToneEmoji(activeSkinTone);
    this.updateSearchString();
    this.setActiveTop('search-container');
  }

  setActiveSkinToneEmoji(activeSkinTone: number) {
    if (activeSkinTone === 0) {
      this.activeSkinToneEmoji.set('1F44D');
    } else if (activeSkinTone === 1) {
      this.activeSkinToneEmoji.set('1F44D-1F3FB');
    } else if (activeSkinTone === 2) {
      this.activeSkinToneEmoji.set('1F44D-1F3FC');
    } else if (activeSkinTone === 3) {
      this.activeSkinToneEmoji.set('1F44D-1F3FD');
    } else if (activeSkinTone === 4) {
      this.activeSkinToneEmoji.set('1F44D-1F3FE');
    } else if (activeSkinTone === 5) {
      this.activeSkinToneEmoji.set('1F44D-1F3FF');
    }
  }

  onEmojiClick(emojiUnicode: string, event) {
    event.preventDefault();

    this.clickedEmoji.unicode = emojiUnicode;
    this.clickedEmoji.native = this.unifiedToNative(emojiUnicode);

    if (event instanceof KeyboardEvent) {
      this.clickedEmoji.isKeyboard = true;
    }

    this.emojiOutput.emit(this.clickedEmoji);

    if (!this.frequentlyUsed()) {
      // Defer the change to the next change detection cycle to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.frequentlyUsed.set(true);
      }, 0);
    }

    const emojis = this.frequentlyUsedEmojis();
    for (var i = 0; i < emojis.length; i++) {
      if (emojis[i].emojiString === emojiUnicode) {
        emojis[i].timesClicked++;
        this.frequentlyUsedEmojis.set([...emojis]);
        this.sortFrequentlyUsedEmojis();
        localStorage.setItem('clComponentChatFrequentlyUsedEmojis', JSON.stringify(this.frequentlyUsedEmojis()));
        return;
      }
    }
    if (emojis.length < 21) {
      // Change this value to change the maximum number of emojis in frequently used category
      var newEmoji: FrequentlyUsedEmoji = {
        timesClicked: 1,
        emojiString: emojiUnicode
      };
      this.frequentlyUsedEmojis.set([...emojis, newEmoji]);
      this.sortFrequentlyUsedEmojis();
    } else {
      emojis[emojis.length - 1].emojiString = emojiUnicode;
      emojis[emojis.length - 1].timesClicked = 1;
      this.frequentlyUsedEmojis.set([...emojis]);
    }
    localStorage.setItem('clComponentChatFrequentlyUsedEmojis', JSON.stringify(this.frequentlyUsedEmojis()));
  }

  unifiedToNative(unified: string) {
    // Returns the native emoji (ex : "😀")
    const codePoints = unified.split('-').map(u => parseInt(`0x${u}`, 16));
    return String.fromCodePoint(...codePoints);
  }

  sortFrequentlyUsedEmojis() {
    const emojis = this.frequentlyUsedEmojis();
    const sorted = [...emojis].sort((a, b) => (a.timesClicked > b.timesClicked ? -1 : 1));
    this.frequentlyUsedEmojis.set(sorted);
  }

  setActiveCategory() {
    let newActiveCategory = false;
    for (var i = 0; i < this.emojiBottomContainer.nativeElement.children.length; i++) {
      if (this.activeSearchResults()) {
        if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('active')) {
          this.emojiBottomContainer.nativeElement.children[i].classList.remove('active'); // Remove active classes if there is an active search result
        }
      } else {
        if (
          this.frequentlyUsedEmojis.length === 0 &&
          this.emojiBottomContainer.nativeElement.children[1].classList.contains('visible')
        ) {
          // Set people to active if recent is empty
          if (this.emojiBottomContainer.nativeElement.children[0].classList.contains('active')) {
            this.emojiBottomContainer.nativeElement.children[0].classList.remove('active'); // Make sure recent is not active if not visible
          }
          if (!this.emojiBottomContainer.nativeElement.children[1].classList.contains('active')) {
            this.emojiBottomContainer.nativeElement.children[1].classList.add('active'); // Make recent active if visible
            newActiveCategory = true;
          }
        }
        if (i === 0) {
          if (this.emojiBottomContainer.nativeElement.children[0].classList.contains('visible')) {
            if (!this.emojiBottomContainer.nativeElement.children[0].classList.contains('active')) {
              this.emojiBottomContainer.nativeElement.children[0].classList.add('active'); // Make recent active if visible
              newActiveCategory = true;
            }
          } else {
            if (this.emojiBottomContainer.nativeElement.children[0].classList.contains('active')) {
              this.emojiBottomContainer.nativeElement.children[0].classList.remove('active'); // Make sure recent is not active if not visible
            }
          }
        }
        if (i > 0) {
          if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('visible')) {
            if (this.emojiBottomContainer.nativeElement.children[i - 1].classList.contains('visible')) {
              if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('active')) {
                this.emojiBottomContainer.nativeElement.children[i].classList.remove('active'); // Make sure category is not active if previous category is visible
              }
            } else if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('visible')) {
              if (!this.emojiBottomContainer.nativeElement.children[i].classList.contains('active')) {
                this.emojiBottomContainer.nativeElement.children[i].classList.add('active'); // Make category active if visible (since it will be the top visible one)
                newActiveCategory = true;
              }
            }
          } else {
            if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('active')) {
              this.emojiBottomContainer.nativeElement.children[i].classList.remove('active'); // Make sure category is not active if not visible
            }
          }
        }
      }
    }

    if (!this.activeSearchResults) {
      let hasActiveCategory = false;
      for (var i = 0; i < this.emojiBottomContainer.nativeElement.children.length; i++) {
        if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('active')) {
          hasActiveCategory = true;
          break;
        }
      }

      if (!hasActiveCategory) {
        // Observer failed
        this.manuallyCheckCategories();
      }
    }
  }

  manuallyCheckCategories() {
    let checkActiveCategory: boolean = false;
    for (var i = 0; i < this.emojiBottomContainer.nativeElement.children.length; i++) {
      if (this.checkInView(this.categoryContainer.toArray()[i].nativeElement)) {
        this.emojiBottomContainer.nativeElement.children[i].classList.add('visible');
        checkActiveCategory = true;
      } else if (this.emojiBottomContainer.nativeElement.children[i].classList.contains('visible')) {
        this.emojiBottomContainer.nativeElement.children[i].classList.remove('visible');
        checkActiveCategory = true;
      }
    }
    if (checkActiveCategory) {
      this.setActiveCategory();
    }
  }

  checkInView(elem) {
    var container = this.emojiScroller.nativeElement;
    var contHeight = container.clientHeight;
    var contTop = container.scrollTop;
    var contBottom = contHeight + contTop;

    var elemHeight = elem.clientHeight;
    var elemTop = elem.offsetTop - container.offsetTop;
    var elemBottom = elemHeight + elemTop;

    var isVisible = contBottom > elemTop && contTop < elemBottom;

    return isVisible;
  }

  jumpToCategory(categoryId) {
    this.categoryTopLevel.find(category => category.nativeElement.id == categoryId).nativeElement.scrollIntoView();
  }

  // Uncompress the data coming from emoji-data/emojis.ts
  uncompress(list: CompressedEmojiData[]) {
    this.emojis = list.map(emoji => {
      const data: any = { ...emoji };
      if (!data.shortNames) {
        data.shortNames = [];
      }
      data.shortNames.unshift(data.shortName);
      data.id = data.shortName;

      if (!data.skinVariations) {
        data.skinVariations = [];
      }

      if (!data.keywords) {
        data.keywords = [];
      }

      if (!data.emoticons) {
        data.emoticons = [];
      }

      if (!data.hidden) {
        data.hidden = [];
      }

      if (!data.text) {
        data.text = '';
      }

      if (data.obsoletes) {
        // get keywords from emoji that it obsoletes since that is shared
        const f = list.find(x => x.unified === data.obsoletes);
        if (f) {
          if (f.keywords) {
            data.keywords = [...data.keywords, ...f.keywords, f.shortName];
          } else {
            data.keywords = [...data.keywords, f.shortName];
          }
        }
      }

      this.names[data.unified] = data;
      for (const n of data.shortNames) {
        this.names[n] = data;
      }
      return data;
    });
  }

  getSanitizedData(emoji: string | EmojiData, skin?: Emoji['skin']) {
    return this.sanitize(this.getData(emoji, skin));
  }

  sanitize(emoji: EmojiData | null): EmojiData | null {
    if (emoji === null) {
      return null;
    }
    const id = emoji.id || emoji.shortNames[0];
    let colons = `:${id}:`;
    if (emoji.skinTone) {
      colons += `:skin-tone-${emoji.skinTone}:`;
    }
    emoji.colons = colons;
    return { ...emoji };
  }

  getData(emoji: EmojiData | string, skin?: Emoji['skin']): EmojiData | null {
    let emojiData: any;

    if (typeof emoji === 'string') {
      const matches = emoji.match(COLONS_REGEX);

      if (matches) {
        emoji = matches[1];

        if (matches[2]) {
          skin = parseInt(matches[2], 10) as Emoji['skin'];
        }
      }
      if (this.names.hasOwnProperty(emoji)) {
        emojiData = this.names[emoji];
      } else {
        return null;
      }
    } else if (emoji.id) {
      emojiData = this.names[emoji.id];
    } else if (emoji.unified) {
      emojiData = this.names[emoji.unified.toUpperCase()];
    }

    if (!emojiData) {
      emojiData = emoji;
      emojiData.custom = true;
    }

    const hasSkinVariations = emojiData.skinVariations && emojiData.skinVariations.length;
    if (hasSkinVariations && skin && skin > 1) {
      emojiData = { ...emojiData };

      const skinKey = SKINS[skin - 1];
      const variationData = emojiData.skinVariations.find((n: EmojiVariation) => n.unified.includes(skinKey));

      if (!variationData.hidden) {
        emojiData.skinTone = skin;
        emojiData = { ...emojiData, ...variationData };
      }
      emojiData.native = this.unifiedToNative(emojiData.unified);
    }

    return emojiData as EmojiData;
  }

  clearSearch() {
    this.searchString.set('');
    this.updateSearchString();
  }

  updateSearchString() {
    this.searchResults.set([]);
    this.noResults.set(false);
    let results: EmojiData[] | undefined;
    if (this.searchString() !== '') {
      this.activeSearchResults.set(true);
      results = this.search(this.searchString(), 49);
      const searchResults: string[] = [];
      for (var i = 0; i < results.length; i++) {
        // Removes obsolete emojis and applies correct skin tone
        if (!results[i].hasOwnProperty('obsoletedBy')) {
          if (results[i].skinVariations.length > 0 && this.activeSkinTone() > 0) {
            const categories = this.categoriesList();
            for (var c = 0; c < categories.length; c++) {
              for (var k = 0; k < categories[c].emojis.length; k++) {
                if (categories[c].emojis[k].unified === results[i].unified) {
                  searchResults.push(categories[c].emojis[k].skinVariations[this.activeSkinTone() - 1].unified);
                  break;
                }
              }
            }
          } else {
            searchResults.push(results[i].unified);
          }
        }
      }
      this.searchResults.set(searchResults);
      if (this.searchResults().length === 0) {
        this.noResults.set(true);
      }
    } else {
      this.activeSearchResults.set(false);
    }

    // Force the [ngClass]="{ hidden: ... }" bindings to flush before querying visibility below -
    // otherwise ensureRovingTabindex would read the pre-toggle DOM state (see Story 05's
    // hidden-container/focus-timing bug for the same class of issue). Guarded because
    // updateSearchString() can run from ngOnInit (via setActiveSkinTone), before the
    // @ViewChild containers are resolved in ngAfterViewInit.
    if (this.emojiScroller && this.emojiSearchResultsContainer) {
      this.cdRef.detectChanges();
      this.ensureRovingTabindex(this.emojiScroller.nativeElement);
      this.ensureRovingTabindex(this.emojiSearchResultsContainer.nativeElement);
    }
  }

  search(value: string, maxResults: number): EmojiData[] | null {
    // Mostly taken from ngx-emoji-mart
    let results: EmojiData[] | undefined;
    let pool = this.originalPool; // this.originalPool is an array of all the emojis (excluding skin variations)

    if (value.length) {
      if (value === '-' || value === '-1') {
        // special case for minus emoji (thumbs down)
        return [this.emojisList['-1']];
      }
      if (value === '+' || value === '+1') {
        // special case for plus emoji (thumbs up)
        return [this.emojisList['+1']];
      }

      let values = value.toLowerCase().split(/[\s|,|\-|_]+/); // normalize search string and splits it at each whitespaces, commas, hyphens and underscore
      let allResults = [];

      if (values.length > 2) {
        values = [values[0], values[1]]; // limits values to 2 (Ex: value:"one two three" --> values:["one","two","three"] --> values:["one","two"])
      }

      allResults = values
        .map(v => {
          let aPool = pool;
          let aIndex = this.index;
          let length = 0;

          // tslint:disable-next-line: prefer-for-of
          for (let charIndex = 0; charIndex < v.length; charIndex++) {
            const char = v[charIndex];
            length++;
            if (!aIndex[char]) {
              aIndex[char] = {};
            }
            aIndex = aIndex[char];

            if (!aIndex.results) {
              const scores: { [key: string]: number } = {}; // Emojis are given a score depending on how much they correspond to the search string

              aIndex.results = [];
              aIndex.pool = {};

              for (const id of Object.keys(aPool)) {
                const emoji = aPool[id];
                if (!this.emojiSearch[id]) {
                  // this.emojiSearch has no data yet, its normal
                  this.emojiSearch[id] = this.buildSearch(
                    emoji.short_names,
                    emoji.name,
                    emoji.id,
                    emoji.keywords,
                    emoji.emoticons
                  );
                }
                const query = this.emojiSearch[id];
                const sub = v.substr(0, length);
                const subIndex = query.indexOf(sub);

                if (subIndex !== -1) {
                  let score = subIndex + 1;
                  if (sub === id) {
                    score = 0;
                  }

                  aIndex.results.push(this.emojisList[id]);
                  aIndex.pool[id] = emoji;

                  scores[id] = score;
                }
              }

              aIndex.results.sort((a, b) => {
                // Sort the emojis from highest to lowest score
                const aScore = scores[a.id];
                const bScore = scores[b.id];

                return aScore - bScore;
              });
            }

            aPool = aIndex.pool;
          }

          return aIndex.results;
        })
        .filter(a => a);

      if (allResults.length > 1) {
        results = intersect.apply(null, allResults as any);
      } else if (allResults.length) {
        results = allResults[0];
      } else {
        results = [];
      }
    }

    if (results) {
      if (results && results.length > maxResults) {
        // Limits results to maxResults parameter
        results = results.slice(0, maxResults);
      }
    }

    return results || null;
  }

  buildSearch(
    // Determines if the search string matches with the parameters of an emoji
    shortNames: string[],
    name: string,
    id: string,
    keywords: string[],
    emoticons: string[]
  ) {
    const search: string[] = [];

    const addToSearch = (strings: string | string[], split: boolean) => {
      if (!strings) {
        return;
      }

      (Array.isArray(strings) ? strings : [strings]).forEach(str => {
        (split ? str.split(/[-|_|\s]+/) : [str]).forEach(s => {
          s = s.toLowerCase();

          if (!search.includes(s)) {
            search.push(s);
          }
        });
      });
    };

    addToSearch(shortNames, true);
    addToSearch(name, true);
    addToSearch(id, true);
    addToSearch(keywords, true);
    addToSearch(emoticons, false);

    return search.join(',');
  }

  undoSearch() {
    this.searchString.set('');
    this.updateSearchString();
  }

  categoryTrack(index: number, item: any) {
    return item.id;
  }

  trackById(index: number, item: any) {
    return item.unified;
  }

  // Roving tabindex for the emoji grid (WCAG 2.4.3): only one emoji per grid is a tab stop;
  // arrow keys move the DOM focus + roving tabindex between the currently visible emojis.
  onEmojiGridKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('emoji-preview')) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const items = this.getVisibleEmojiItems(container);
    const currentIndex = items.indexOf(target);
    if (currentIndex === -1) {
      return;
    }

    let newIndex = currentIndex;
    switch (event.key) {
      case 'ArrowRight':
        newIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case 'ArrowLeft':
        newIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'ArrowDown':
        newIndex = this.findVerticalNeighbor(currentIndex, items, 1);
        break;
      case 'ArrowUp':
        newIndex = this.findVerticalNeighbor(currentIndex, items, -1);
        break;
      default:
        return;
    }

    // Prevent page scroll (arrow keys) / avoid interfering with other handlers.
    event.preventDefault();
    if (newIndex !== currentIndex) {
      this.setActiveEmojiIndex(items, newIndex);
      items[newIndex].focus();
    }
  }

  // Keeps the roving tabindex in sync when focus lands on an emoji via mouse click or Tab.
  onEmojiGridFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    if (!target.classList.contains('emoji-preview')) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const items = this.getVisibleEmojiItems(container);
    const index = items.indexOf(target);
    if (index !== -1) {
      this.setActiveEmojiIndex(items, index);
    }
  }

  // Ensures exactly one currently-visible emoji in the container is tabbable, defaulting to the
  // first one. Safe to call after any change that can add/remove/show/hide emojis.
  ensureRovingTabindex(container: HTMLElement | undefined): void {
    if (!container) {
      return;
    }
    const items = this.getVisibleEmojiItems(container);
    if (items.length === 0) {
      return;
    }
    const hasActiveItem = items.some(el => el.getAttribute('tabindex') === '0');
    if (!hasActiveItem) {
      this.setActiveEmojiIndex(items, 0);
    }
  }

  private getVisibleEmojiItems(container: HTMLElement): HTMLElement[] {
    // .hidden (recent/category/search containers) sets visibility:hidden rather than
    // display:none, so offsetParent can't be used here - walk up for the .hidden ancestor instead.
    return Array.from(container.querySelectorAll<HTMLElement>('.emoji-preview')).filter(el => !el.closest('.hidden'));
  }

  private setActiveEmojiIndex(items: HTMLElement[], activeIndex: number): void {
    items.forEach((el, i) => el.setAttribute('tabindex', i === activeIndex ? '0' : '-1'));
  }

  // Finds the nearest emoji in the given vertical direction (1 = down, -1 = up) by comparing
  // rendered geometry, so it works across the ragged/variable-width rows of a flex-wrap grid
  // and across category boundaries without assuming a fixed items-per-row.
  private findVerticalNeighbor(currentIndex: number, items: HTMLElement[], direction: 1 | -1): number {
    const current = items[currentIndex].getBoundingClientRect();
    let bestIndex = currentIndex;
    let bestRowDelta = Infinity;
    let bestColDelta = Infinity;

    for (let i = 0; i < items.length; i++) {
      if (i === currentIndex) {
        continue;
      }
      const rect = items[i].getBoundingClientRect();
      const rowDelta = (rect.top - current.top) * direction;
      if (rowDelta <= 0.5) {
        // Not strictly further in the requested direction (0.5px tolerance for sub-pixel layout).
        continue;
      }
      if (rowDelta < bestRowDelta - 0.5) {
        bestRowDelta = rowDelta;
        bestColDelta = Math.abs(rect.left - current.left);
        bestIndex = i;
      } else if (Math.abs(rowDelta - bestRowDelta) <= 0.5) {
        const colDelta = Math.abs(rect.left - current.left);
        if (colDelta < bestColDelta) {
          bestColDelta = colDelta;
          bestIndex = i;
        }
      }
    }

    return bestIndex;
  }
}
